'use client'

import type { SearchShortcutsProps } from '@maestro/commerce-blocks/components/search-bar/search.types'

import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import { getSearchOptionId } from '@maestro/commerce-blocks/utils/search.utils'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer'
import { Link } from '@pearlconvert/ui/components/link'

export default function SearchShortcuts({
  searchShortcuts,
  hideSearch,
  activeOptionId,
}: SearchShortcutsProps) {
  const t = useTranslations('commerce-blocks')
  const { publish } = useEventBusContext()

  const handleShortcutClick = useCallback(
    (title: string, url: string) => {
      publish(EventType.SEARCH_SUGGESTIONS_CLICKED, {
        searchSection: `${t('_globals.shortcuts')}`,
        searchTerm: title,
        clickUrl: url,
      })
      hideSearch?.()
    },
    [hideSearch, publish, t]
  )

  const shortcutsList = useMemo(() => {
    return searchShortcuts.map((shortcut, index) => {
      const optionId = getSearchOptionId('shortcuts', shortcut.title, index)

      return (
        <li
          className="searchbar__suggestions-list-item"
          key={`SearchShortcuts-${shortcut.title}-${index}`}
          id={optionId}
          role="option"
          aria-selected={activeOptionId === optionId}
        >
          <Link
            className="searchbar__suggestions-list-link"
            href={shortcut.url}
            tabIndex={-1}
            aria-label={`${t('_globals.shortcuts')}: ${shortcut.title}`}
            onClick={() => handleShortcutClick(shortcut.title, shortcut.url)}
          >
            {shortcut.title}
          </Link>
        </li>
      )
    })
  }, [activeOptionId, handleShortcutClick, searchShortcuts, t])

  return (
    <div className="searchbar__suggestions-node">
      <h5 className="searchbar__suggestions-title">
        {t('_globals.shortcuts')}
      </h5>
      <ul className="searchbar__suggestions-list searchbar__suggestions-list--simple">
        {shortcutsList}
      </ul>
    </div>
  )
}
